/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useMemo } from 'react';
import { useTheme } from '@mui/material';
import { getPipelineIcon, getUrlFromPipeline, DEAL_STATUS_TYPES } from '@/utils/constants';
import { useNavigate } from 'react-router-dom';
import { Configure, InstantSearch, useHits } from 'react-instantsearch';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useSelector } from 'react-redux';
import { usePipelineContext } from '@/context/pipelineContext';
import { clientSearch } from '@/utils/typesense/clients/clientSearch';
import { dealSearch } from '@/utils/typesense/clients/dealSearch';
import { mapStageIds } from '@/hooks/useInstantSearchOwnerFilters';
import DashboardCard from '../DashboardCard';

const pipelineStrings = [
	process.env.REACT_APP_PIPELINE_NEW_BUSINESS,
	process.env.REACT_APP_PIPELINE_RENEWALS,
	process.env.REACT_APP_PIPELINE_ENDORSEMENTS,
].join(',');

export const InstantDealDisplayCard = ({ pipelineId, title }) => {
	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { groupView } = useSelector((state) => state.userPrefs);

	const isEndorsement = pipelineId === process.env.REACT_APP_PIPELINE_ENDORSEMENTS;

	const isRenewals = pipelineId === process.env.REACT_APP_PIPELINE_RENEWALS;

	const { active, inactive, stages } = usePipelineContext(
		isEndorsement || isRenewals ? null : pipelineId,
	);

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const userId = userDetails?.userId ?? 0;
	const brokerGroupIds = userDetails?.brokerGroupArray;

	const ownerFilter = groupView
		? `brokerGroupId: [${brokerGroupIds.join(',')}]`
		: `ownerDocId := ${userId}`;

	const endorsementStages = stages
		?.filter(
			(s) =>
				[
					process.env.REACT_APP_PIPELINE_NEW_BUSINESS,
					process.env.REACT_APP_PIPELINE_RENEWALS,
				].includes(s.pipelineId) && s.label?.toLowerCase()?.includes('endorsement'),
		)
		?.map((s) => s.id);

	// Active (not closed) stages include the invoiced stage, which isn't in progress
	const inProgressStageIds = useMemo(() => {
		const inactiveStageIds = mapStageIds(inactive);
		return mapStageIds(active).filter((stageId) => !inactiveStageIds.includes(stageId));
	}, [active, inactive]);

	const filterSchema = useMemo(() => {
		const hideArchived = `status:!=${DEAL_STATUS_TYPES.archived}`;
		const activeStages = `dealStage: [${active.join(',')}]`;

		if (pipelineId === process.env.REACT_APP_PIPELINE_CLAIMS) {
			return {
				filters: `pipeline:=${process.env.REACT_APP_PIPELINE_CLAIMS} && ${ownerFilter} && ${activeStages} && ${hideArchived}`,
			};
		}

		if (pipelineId === process.env.REACT_APP_PIPELINE_RENEWALS) {
			const ren = `(pipeline:=${process.env.REACT_APP_PIPELINE_RENEWALS} && ${hideArchived})`;

			return {
				filters: `(${ren} && dealStage: [${inProgressStageIds.join(',')}]) && ${ownerFilter}`,
			};
		}

		if (isEndorsement) {
			const endorsementStageIds = [
				...(endorsementStages || []),
				...active
					.filter((c) => c.pipelineId === process.env.REACT_APP_PIPELINE_ENDORSEMENTS)
					.map((c) => c.id)
					.filter((stageId) => inProgressStageIds.includes(stageId)),
			];
			const endorsements = `(pipeline: [${pipelineStrings}] && dealStage: [${endorsementStageIds.join(
				',',
			)}] && status:!=${DEAL_STATUS_TYPES.archived})`;

			return {
				filters: `${endorsements} && ${ownerFilter}`,
			};
		}

		return {
			filters: `pipeline: [${pipelineStrings}] && dealStage: [${inProgressStageIds.join(
				',',
			)}] && ${ownerFilter} && ${hideArchived} && dealStage:!= [${endorsementStages.join(
				',',
			)}]`,
		};
	}, [
		ownerFilter,
		active,
		inProgressStageIds,
		endorsementStages,
		pipelineId,
		pipelineStrings,
	]);

	return (
		<InstantSearch searchClient={dealSearch} indexName='deals'>
			<Configure hitsPerPage={15} {...filterSchema} />
			<InstantDealDisplayCardWrapper title={title} pipelineId={pipelineId} />
		</InstantSearch>
	);
};

export const InstantProspectDisplayCard = ({ title, url }) => {
	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const { groupView } = useSelector((state) => state.userPrefs);
	const { active } = usePipelineContext(process.env.REACT_APP_PIPELINE_PROSPECTS);

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const userId = userDetails?.userId ?? 0;
	const brokerGroupIds = userDetails?.brokerGroupArray;

	const ownerFilter = groupView
		? `brokerGroupId: [${brokerGroupIds.join(',')}]`
		: `ownerDocId := ${userId}`;

	const filterSchema = useMemo(() => {
		const openStageIds = (active ?? []).map((stageId) => `'${stageId}'`);
		const stageFilter = openStageIds.length > 0 ? ` && stage:[${openStageIds.join(',')}]` : '';
		const filters = `${ownerFilter} && isProspect:= true${stageFilter}`;
		return { filters };
	}, [ownerFilter, active]);

	return (
		<InstantSearch indexName='clients' searchClient={clientSearch}>
			<Configure hitsPerPage={15} {...filterSchema} />
			<InstantDealDisplayCardWrapper title={title} pipelineId={'Prospects'} url={url} />
		</InstantSearch>
	);
};

const InstantDealDisplayCardWrapper = ({ pipelineId, title, ...props }) => {
	const theme = useTheme();
	const { results } = useHits(props);

	const total = results.nbHits;

	const navigate = useNavigate();
	const url = useMemo(() => getUrlFromPipeline(pipelineId), [pipelineId]);

	const handleClick = () => navigate(url);

	return (
		<DashboardCard
			handleClick={handleClick}
			title={`Active ${title}`}
			Icon={() =>
				getPipelineIcon(
					pipelineId,
					'50px',
					theme.palette.mode === 'dark' ? { bgColor: 'transparent' } : undefined,
				)
			}
			total={total}
		/>
	);
};
